// Measures the response gap of a voice call the way the caller hears it:
// from the moment they stop talking to the moment Sadie's voice starts. It
// reads audio levels, not engine events, so Live and Realtime are timed the
// same way.

// RMS levels (0..1) above which a frame counts as sound. The mic runs through
// the browser's noise suppression, so room hum sits well below.
const MIC_THRESHOLD = 0.02;
const SADIE_THRESHOLD = 0.01;
// Sadie counts as done talking after this much quiet, so a pause between her
// sentences is not a new turn.
const SADIE_QUIET_MS = 700;

export interface VoiceGap {
  // You stop talking -> Sadie's voice starts.
  totalMs: number;
  // Realtime only: you stop talking -> the engine commits your turn. The rest
  // of the gap is the model producing its first audio.
  turnDetectionMs?: number;
}

export interface VoiceGapTracker {
  /** One audio frame. Returns a gap when Sadie starts answering. */
  tick: (now: number, micLevel: number, sadieLevel: number) => VoiceGap | null;
  /** The engine committed the caller's turn (Realtime's input_audio_buffer.committed). */
  markCommitted: (now: number) => void;
}

export const createVoiceGapTracker = (): VoiceGapTracker => {
  let lastUserSoundAt: number | null = null;
  let committedAt: number | null = null;
  let sadieSpeaking = false;
  let lastSadieSoundAt = 0;

  return {
    tick: (now, micLevel, sadieLevel) => {
      if (sadieLevel > SADIE_THRESHOLD) {
        lastSadieSoundAt = now;
        if (!sadieSpeaking) {
          sadieSpeaking = true;
          // Sadie opening the call, or talking again with nothing from the
          // caller in between, is no response gap.
          if (lastUserSoundAt === null) return null;
          const gap: VoiceGap = { totalMs: now - lastUserSoundAt };
          if (committedAt !== null && committedAt >= lastUserSoundAt) {
            gap.turnDetectionMs = committedAt - lastUserSoundAt;
          }
          lastUserSoundAt = null;
          committedAt = null;
          return gap;
        }
        return null;
      }
      if (sadieSpeaking && now - lastSadieSoundAt > SADIE_QUIET_MS) {
        sadieSpeaking = false;
      }
      // While Sadie talks, sound on the mic is mostly her own voice leaking
      // back, not the caller.
      if (!sadieSpeaking && micLevel > MIC_THRESHOLD) lastUserSoundAt = now;
      return null;
    },
    markCommitted: (now) => {
      committedAt = now;
    },
  };
};

/** RMS level of one analyser frame. */
export const analyserLevel = (
  analyser: AnalyserNode,
  buffer: Float32Array<ArrayBuffer>,
): number => {
  analyser.getFloatTimeDomainData(buffer);
  let sum = 0;
  for (const sample of buffer) sum += sample * sample;
  return Math.sqrt(sum / buffer.length);
};
